import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetLastPayout } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetLastPayout";
import { useGetPayoutSettings } from "@src/appV2/redesign/Accounts/PayoutSettings/api/useGetPayoutSettings";

import { usePayoutRemainingBalance } from "../../api/usePayoutRemainingBalance";
import { useGetWorkerPayments } from "../api/useGetWorkerPayments";
import { useGetWorkerPaymentsStats } from "../api/useGetWorkerPaymentsStats";
import { OutstandingBalanceBanner } from "./OutstandingBalanceBanner";

export interface OutstandingBalanceBannerContainerProps {
  sx?: SxProps<Theme>;
}

export function OutstandingBalanceBannerContainer(props: OutstandingBalanceBannerContainerProps) {
  const { sx } = props;

  const workerPaymentsStats = useGetWorkerPaymentsStats();
  const lastPayout = useGetLastPayout();
  const payoutSettings = useGetPayoutSettings();
  const workerPayments = useGetWorkerPayments();

  const payoutRemainingBalance = usePayoutRemainingBalance({
    onPayoutSuccess: async () => {
      await workerPaymentsStats.refetch();
    },
  });

  const availableBalanceInCents =
    workerPaymentsStats.data?.data.attributes.availableBalanceInCents ?? 0;

  const instantPayFeeInCents = isDefined(payoutSettings.data)
    ? Math.round(payoutSettings.data.meta.instantPayoutFee * 100)
    : undefined;

  const payoutSchedule = isDefined(payoutSettings.data)
    ? payoutSettings.data.attributes.payoutSchedule
    : undefined;

  const isBannerShown =
    // balance is greater than instant pay fee
    isDefined(instantPayFeeInCents) &&
    availableBalanceInCents > instantPayFeeInCents &&
    // last payout is not failed - if it is, we must show FailedPayoutBannerContainer
    isDefined(lastPayout.data) &&
    lastPayout.data.data.attributes.status !== "failed" &&
    // TODO(VAU-561): after this ticket is done, delete the rest of the condition
    (payoutSchedule === "instant" ||
      (isDefined(workerPayments.data) &&
        workerPayments.data.pages[0]?.data[0]?.attributes.payoutStatus === "paid"));

  useLogEffect(
    APP_V2_USER_EVENTS.EARNINGS_PAGE_OUTSTANDING_BALANCE_BANNER_SHOWN,
    { availableBalanceInCents },
    { enabled: isBannerShown }
  );

  if (!isBannerShown) {
    return null;
  }

  return (
    <Box sx={sx}>
      <OutstandingBalanceBanner
        availableBalanceInCents={availableBalanceInCents}
        payoutSchedule={payoutSchedule}
        isPayingOutBalance={payoutRemainingBalance.isLoading}
        onClick={() => {
          logEvent(
            APP_V2_USER_EVENTS.EARNINGS_PAGE_OUTSTANDING_BALANCE_BANNER_SEND_PAYMENT_CLICKED,
            { availableBalanceInCents }
          );
          payoutRemainingBalance.mutate();
        }}
      />
    </Box>
  );
}
